import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';

import { useState, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { AI_ACTIONS } from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonSaveDrawer,
	setSimulationCommonShareDrawer,
} from 'common/store/reducer/commonReducer';
import {
	addUsersToStateList,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import UsersListSelection from 'features/Settings/Components/UsersListSelection/UsersListSelection';

import {
	ISimulationSaveDrawer,
	ISimulationSaveSliderModel,
} from '../../api/type-def';

import styles from './SaveSimulationDetails.module.scss';

const { SAVE, SHARE } = AI_ACTIONS;

const SaveSimulationDetails = (props: ISimulationSaveDrawer) => {
	const { saveSimulation, type, shareSimulation, name, existingSharedData } =
		props;

	const { drawerCloseIconColor } = styles;

	const {
		control,
		getValues,
		formState: { isValid },
	} = useForm<ISimulationSaveSliderModel>({
		mode: 'onChange',
		defaultValues: {
			simulationName: name,
		},
	});

	const dispatch = useDispatch();

	const [selectedUsers, setSelectedUsers] = useState<any>();
	const [existingUsersData, setexistingUsersData] = useState<any>();
	useEffect(() => {
		if (existingSharedData?.recipients?.length > 0) {
			const existingUsersList = addUsersToStateList(
				existingSharedData?.recipients,
			);
			setexistingUsersData(existingUsersList);
		} else {
			setexistingUsersData([]);
		}
	}, [existingSharedData]);

	const isSaveBtnDisable =
		!isValid ||
		(type === SHARE && (!selectedUsers || selectedUsers.length === 0));

	const saveSimulationAllModal = useSelector(
		(state: RootState) => state.common.simulationCommonSaveDrawer,
	);

	const shareSimulationAllModal = useSelector(
		(state: RootState) => state.common.simulationCommonShareDrawer,
	);

	const drawerCloseHandler = useCallback(() => {
		if (type === SAVE) {
			dispatch(setSimulationCommonSaveDrawer(false));
		} else if (type === SHARE) {
			dispatch(setSimulationCommonShareDrawer(false));
		}
	}, [saveSimulationAllModal, shareSimulationAllModal]);

	const onSaveHandler = async () => {
		const formData = getValues();
		if (type === SAVE && saveSimulation) {
			saveSimulation(formData.simulationName);
		} else if (type === SHARE && shareSimulation) {
			shareSimulation(
				formData.simulationName,
				formData?.simulationComment ?? '',
				selectedUsers,
			);
		}
	};

	const onCancelHandler = () => {
		drawerCloseHandler();
	};

	const onUserListSelect = (data: any) => {
		setSelectedUsers(data);
	};

	return (
		<Box role="presentation" data-testid="simulation-container">
			<div className={styles['simulation-top-div']}>
				<div
					className={styles['simulation-text-background']}
					data-testid="simulation-top-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="large"
							onClick={drawerCloseHandler}
							data-testid="close-simulation-btn"
						>
							<DrawerClose fill={drawerCloseIconColor} />
						</IconButton>
					</Stack>

					<Box className={styles['simulation-text-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['simulation-text-name']}
								data-testid="save-text"
							>
								{type === SAVE
									? toSentenceCase('Save Simulation')
									: toSentenceCase('Share Simulation')}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>
			<Grid
				item
				className={styles['simulation-form-layout']}
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
			>
				<form>
					<Grid container>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="simulationName"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please enter Simulation name',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldWithLabel
										label={toSentenceCase('Simulation Name')}
										name={name}
										id="task-name"
										placeholder={toSentenceCase('Enter the Simulation name')}
										value={value}
										inputProps={{
											'data-testid': 'Simulation-name',
										}}
										onChange={onChange}
										error={!!error}
										required
										helperText={error ? error.message : null}
										className={styles['simulation-field-width']}
										positionClass={styles['word-limit-position']}
										wordLimit={100}
									/>
								)}
							/>
						</Grid>
						{type === SHARE && (
							<>
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									xl={12}
									lg={12}
									className={styles['recepients-email-width']}
								>
									<UsersListSelection
										onUserListItemSelect={onUserListSelect}
										existingUsersData={existingUsersData}
										actionType={existingSharedData ? 'edit' : 'create'}
										hideLoggedInUser
										sharedBy={existingSharedData?.sharedBy}
										title={
											<div>
												Recipients{' '}
												<span className={styles['recepients-mandatory']}>
													*
												</span>
											</div>
										}
									/>
									{selectedUsers?.length === 0 && (
										<div className={styles['recepients-error']}>
											Please add atleast 1 recipient
										</div>
									)}
								</Grid>
								<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
									<Controller
										name="simulationComment"
										control={control}
										rules={{
											required: {
												value: false,
												message: 'Please enter Comments',
											},
										}}
										render={({
											field: { name, value, onChange },
											fieldState: { error },
										}) => (
											<TextFieldWithLabel
												label="Comments"
												name={name}
												id="comments"
												inputProps={{
													'data-testid': 'commentsId',
												}}
												placeholder="Enter the comment"
												value={value}
												onChange={onChange}
												error={!!error}
												helperText={error ? error.message : null}
												className={styles['comment-field-width']}
											/>
										)}
									/>
								</Grid>
							</>
						)}
					</Grid>
				</form>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={
					styles[type === SAVE ? 'btn-position' : 'share-btn-position']
				}
			>
				<Stack
					direction="row"
					className={styles['btn-save-button']}
					spacing={2}
				>
					<ButtonComp
						btnType="secondary"
						data-testid="cancel-btn"
						clickHandler={onCancelHandler}
						compClass={styles['btn-cancel']}
					>
						Cancel
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						disabled={isSaveBtnDisable}
						clickHandler={onSaveHandler}
						data-testid="save-btn"
					>
						{type === SAVE ? 'Save' : 'Share & save'}
					</ButtonComp>
				</Stack>
			</Grid>
		</Box>
	);
};

export default SaveSimulationDetails;
